// ============================================================
// Shell tokens + typography scale + visual helpers.
//
// Aligned to Square Code · Brand Guide v2.
//
//   Foundation:  #12091f deep purple-black (readability pass 2026-07-05)
//   Substrate:   purple rgba(123,94,167,*) — inputs, panels, borders
//   Accents:     8 colors, each owning a meaning
//   Type:        Instrument Sans (UI) · Playfair (display) · JetBrains Mono
//   Glow:        never decorative — always means active/live/meaningful
//
// SHELL_TOKENS export surface is stable — only values change. This
// retargets the 700+ existing references across 43 files onto the
// Brand Guide v2 palette without requiring a full sweep.
// ============================================================

import type { CSSProperties } from 'react'

// ----------------------------------------------------------------
// Brand Guide v2 canonical literals — the single source for the
// retargeted SHELL_TOKENS values below. Keep these in sync with
// packages/square-ui/src/tokens.css (--sc-* surface).
// ----------------------------------------------------------------

const V2 = {
  // Readability pass (operator-directed, 2026-07-05): void lifted to
  // deep purple-black; substrate alphas + text opacities raised so
  // text and controls read clearly. Palette unchanged — same purple
  // substrate hue, same 8 accents. Keep in sync with tokens.css.
  bg:         '#12091f',
  surface1:   'rgba(123, 94, 167, 0.06)',
  surface2:   'rgba(123, 94, 167, 0.09)',
  surface3:   'rgba(123, 94, 167, 0.12)',
  surface4:   'rgba(123, 94, 167, 0.16)',
  surface5:   'rgba(123, 94, 167, 0.20)',

  borderRest:   'rgba(123, 94, 167, 0.16)',
  borderHover:  'rgba(123, 94, 167, 0.24)',
  borderActive: 'rgba(123, 94, 167, 0.34)',
  borderFocus:  'rgba(123, 94, 167, 0.44)',

  textPrimary:   '#f2f4ff',
  textSecondary: '#b4bfd8',
  textTertiary:  '#8b93ad',
  textMuted:     'rgba(255, 255, 255, 0.34)',

  // Accents
  orange: '#E8854A',
  cyan:   '#00aad2',
  green:  '#00a67d',
  red:    '#e85d3a',
  purple: '#7b5ea7',
  gold:   '#c4a24e',
  blue:   '#5B9FD4',
  pink:   '#C97BAD',
} as const

// ----------------------------------------------------------------
// SHELL_TOKENS — stable names, Brand Guide v2 values.
//
// Every name below is load-bearing for existing consumers. Keep
// the keys; retarget only the values. Anywhere a consumer was
// reading e.g. SHELL_TOKENS.brand it now gets orange (Identity),
// which is the Brand Guide v2 primary accent.
// ----------------------------------------------------------------

export const SHELL_TOKENS = {
  // ── Foundation ──
  void:          V2.bg,
  voidAlt:       V2.bg,
  ink:           V2.textPrimary,
  inkDim:        V2.textSecondary,
  inkMuted:      V2.textSecondary,
  inkFaint:      V2.textTertiary,
  inkWhisper:    V2.textTertiary,
  inkGhost:      V2.textMuted,

  // ── Surface scale ──
  surface:       V2.surface1,
  surfaceAlt:    V2.surface2,
  surfaceRaised: V2.surface3,
  surfaceHigh:   V2.surface4,

  // ── Borders (purple substrate) ──
  border:        V2.borderRest,
  borderAlt:     V2.borderHover,
  borderHigh:    V2.borderActive,

  // ── Brand accent (Identity = Orange per Brand Guide v2) ──
  brand:         V2.orange,
  brandSoft:     'rgba(232, 133, 74, 0.08)',
  brandLine:     'rgba(232, 133, 74, 0.30)',
  brandGlow:     'rgba(232, 133, 74, 0.40)',

  // ── 8 accent meanings ──
  orange:        V2.orange,
  cyan:          V2.cyan,
  blue:          V2.blue,
  green:         V2.green,
  amber:         V2.gold,   // 'amber' preserved as name; value is gold
  gold:          V2.gold,
  red:           V2.red,
  purple:        V2.purple,
  pink:          V2.pink,

  // ── Preserved legacy names that map onto the v2 palette ──
  teal:          V2.cyan,
  rose:          V2.red,
  indigo:        V2.purple,

  // ── Text-on-accent helpers (subtle tint for status chips) ──
  greenText:     '#a8efbf',
  amberText:     '#ffd9a8',
  redText:       '#ffb3a0',
} as const

// ----------------------------------------------------------------
// Typography scale — Brand Guide v2
//
// Every piece of text in the shell chrome should come from one of
// these roles. Brand Guide rules enforced:
//   • Display/hero: Playfair Display or Instrument Sans (never Inter/Roboto)
//   • Minimum body 14px · minimum label 10px · minimum visible 9px
//   • Section labels: JetBrains Mono 11px/700/3-4px uppercase
//   • Page title: 32px weight 200 letter-spacing -1px
// ----------------------------------------------------------------

export const text = {
  // Section kicker — Brand Guide "── SQUARE CODE · HOSTING" label.
  // JetBrains Mono 11px/700/3-4px letter-spacing uppercase.
  kicker: {
    fontSize: 11,
    letterSpacing: '0.26em',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: SHELL_TOKENS.inkFaint,
    fontFamily: 'var(--sc-font-mono, var(--font-mono))',
  } as CSSProperties,

  // Micro/nano labels — timestamps, footnotes. Minimum 9px.
  micro: {
    fontSize: 9,
    letterSpacing: '0.18em',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: SHELL_TOKENS.inkFaint,
    fontFamily: 'var(--sc-font-mono, var(--font-mono))',
  } as CSSProperties,

  nano: {
    fontSize: 10,
    letterSpacing: '0.12em',
    fontWeight: 700,
    fontFamily: 'var(--sc-font-mono, var(--font-mono))',
  } as CSSProperties,

  // Row meta — secondary info beneath row titles
  rowMeta: {
    fontSize: 12,
    color: SHELL_TOKENS.inkFaint,
    fontFamily: 'var(--sc-font-mono, var(--font-mono))',
  } as CSSProperties,

  // Row primary — client/event/session names. Card title spec.
  rowTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: SHELL_TOKENS.ink,
    letterSpacing: 0,
    fontFamily: 'var(--sc-font-ui, var(--font-body))',
  } as CSSProperties,

  // Body copy. Minimum 14px per Brand Guide — we use 15.
  body: {
    fontSize: 15,
    fontWeight: 400,
    color: SHELL_TOKENS.inkDim,
    fontFamily: 'var(--sc-font-ui, var(--font-body))',
  } as CSSProperties,

  // Data value — metric/KPI number. JetBrains Mono 16px+ weight 700.
  dataValue: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: 0,
    color: SHELL_TOKENS.ink,
    fontFamily: 'var(--sc-font-mono, var(--font-mono))',
  } as CSSProperties,

  // Same, kept under the original name for ~40 existing refs.
  metric: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: 0,
    color: SHELL_TOKENS.ink,
    fontFamily: 'var(--sc-font-mono, var(--font-mono))',
  } as CSSProperties,

  // Page title — Brand Guide v2 exact spec. 32px / 200 / -1px.
  editorial: {
    fontSize: 32,
    fontWeight: 200,
    letterSpacing: '-0.03em',
    color: SHELL_TOKENS.ink,
    fontFamily: 'var(--sc-font-ui, var(--font-body))',
  } as CSSProperties,

  // Section title — 22px / 300 / -0.5px
  sectionTitle: {
    fontSize: 22,
    fontWeight: 300,
    color: SHELL_TOKENS.ink,
    letterSpacing: '-0.022em',
    fontFamily: 'var(--sc-font-ui, var(--font-body))',
  } as CSSProperties,

  // Code / data / domains / commit SHAs
  code: {
    fontSize: 13,
    fontWeight: 400,
    letterSpacing: '0.04em',
    color: SHELL_TOKENS.inkDim,
    fontFamily: 'var(--sc-font-mono, var(--font-mono))',
  } as CSSProperties,
} as const

// ----------------------------------------------------------------
// Shell layout styles — consumed by MainframeShell
// ----------------------------------------------------------------

export const shellStyles: Record<string, CSSProperties> = {
  app: {
    minHeight: '100vh',
    background: SHELL_TOKENS.void,
    color: SHELL_TOKENS.ink,
    fontFamily: 'var(--sc-font-ui, var(--font-body))',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  ambient: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    // Brand Guide v2: the OLIVIUM frequency bleeds through as faint
    // purple. No cyan, no amber ambient — single accent per plane.
    background:
      'radial-gradient(circle at 18% 12%, rgba(123, 94, 167, 0.14), transparent 40%), ' +
      'radial-gradient(circle at 82% 88%, rgba(123, 94, 167, 0.08), transparent 40%)',
    zIndex: 0,
  },
  topbar: {
    height: 60,
    borderBottom: `1px solid ${SHELL_TOKENS.borderAlt}`,
    background: 'rgba(18, 9, 31, 0.82)',
    backdropFilter: 'blur(18px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20px',
    position: 'relative',
    zIndex: 2,
    flexShrink: 0,
  },
  body: {
    // display + grid-template-columns live in shell-layout.css
    // (.sc-shell-body) so media queries can collapse the chrome.
    minHeight: 0,
    flex: 1,
    position: 'relative',
    zIndex: 1,
  },
  sidebar: {
    borderRight: `1px solid ${SHELL_TOKENS.borderAlt}`,
    padding: '20px 14px',
    background: 'rgba(20, 11, 35, 0.68)',
    backdropFilter: 'blur(14px)',
    overflowY: 'auto',
  },
  mainCol: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
  },
  main: {
    // padding lives in shell-layout.css (.sc-shell-main) so it can
    // tighten at phone widths.
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  rightPanel: {
    borderLeft: `1px solid ${SHELL_TOKENS.borderAlt}`,
    background: 'rgba(20, 11, 35, 0.76)',
    backdropFilter: 'blur(16px)',
    padding: '20px 18px',
    overflowY: 'auto',
  },
  commandBar: {
    flexShrink: 0,
    borderTop: `1px solid ${SHELL_TOKENS.borderAlt}`,
    background: 'rgba(16, 8, 28, 0.95)',
    backdropFilter: 'blur(20px)',
    padding: '10px 14px',
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    position: 'relative',
    zIndex: 2,
  },
}

// ----------------------------------------------------------------
// Surface helpers — reusable style objects for cards / rows
// ----------------------------------------------------------------

export const surfaces = {
  card: {
    background: SHELL_TOKENS.surface,
    border: `1px solid ${SHELL_TOKENS.border}`,
    borderRadius: 12,
    padding: 16,
  } as CSSProperties,
  row: {
    background: SHELL_TOKENS.surface,
    border: `1px solid ${SHELL_TOKENS.border}`,
    borderRadius: 10,
  } as CSSProperties,
}

// ----------------------------------------------------------------
// Status chip helper — Brand Guide v2 status badge:
// JetBrains Mono 10px/700/1.5px uppercase, 6px radius, color bg 10%.
// ----------------------------------------------------------------

export function chipStyle(accent: string): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 9px',
    borderRadius: 6,
    background: `${accent}1a`,
    border: `1px solid ${accent}33`,
    color: accent,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    fontFamily: 'var(--sc-font-mono, var(--font-mono))',
  }
}

// ----------------------------------------------------------------
// Grid overlay — Brand Guide v2 purple substrate.
// Barely visible. You should question whether you're seeing it.
// 50px × 50px · drifts via sc-grid-drift over 60s.
// ----------------------------------------------------------------

export const GRID_BACKGROUND_URL =
  'url("data:image/svg+xml;utf8,' +
  '<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2250%22 height=%2250%22>' +
  '<path d=%22M50 0H0v50%22 fill=%22none%22 stroke=%22%237b5ea7%22 stroke-width=%220.4%22 stroke-opacity=%220.08%22/>' +
  '</svg>")'

// ----------------------------------------------------------------
// Semantic tones — Brand Guide v2 meaning mapping
// ----------------------------------------------------------------

export function badgeTone(severity?: string) {
  if (severity === 'critical') {
    return {
      bg: 'rgba(232, 93, 58, 0.10)',
      border: 'rgba(232, 93, 58, 0.32)',
      text: V2.red,
    }
  }
  if (severity === 'warning') {
    return {
      bg: 'rgba(196, 162, 78, 0.10)',
      border: 'rgba(196, 162, 78, 0.32)',
      text: V2.gold,
    }
  }
  return {
    bg: 'rgba(0, 170, 210, 0.10)',
    border: 'rgba(0, 170, 210, 0.28)',
    text: V2.cyan,
  }
}

export function statusAccent(status?: string): string {
  switch (status) {
    case 'critical':
    case 'suspicious':
    case 'blocked':
      return SHELL_TOKENS.red
    case 'warning':
    case 'review':
    case 'pending':
    case 'paused':
      return SHELL_TOKENS.gold
    case 'building':
    case 'active':
      return SHELL_TOKENS.blue
    case 'archived':
      return SHELL_TOKENS.inkFaint
    case 'healthy':
    case 'success':
    case 'verified':
      return SHELL_TOKENS.green
    default:
      return SHELL_TOKENS.inkFaint
  }
}

export function healthAccent(health?: number): string {
  if (health == null) return SHELL_TOKENS.inkFaint
  if (health > 70) return SHELL_TOKENS.green
  if (health > 40) return SHELL_TOKENS.gold
  return SHELL_TOKENS.red
}

// ----------------------------------------------------------------
// Time + hash formatters (unchanged)
// ----------------------------------------------------------------

export function timeAgo(ts?: string): string {
  if (!ts) return '—'
  const diff = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 1000))
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

export function shortHash(v?: string): string {
  if (!v) return '—'
  const s = String(v)
  if (s.length <= 16) return s
  return `${s.slice(0, 8)}…${s.slice(-4)}`
}
